import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { X, ChevronDown, RefreshCw, AlertTriangle, Search, MapPin, CheckCircle2, Maximize2, Minimize2, RotateCcw, Copy, Check } from "lucide-react";
import { getVisitNoteJsonDetails } from "../services/masterDataService";

const BRAND_GREEN = "#0aa375";
function safeParseJson(raw) {
    if (raw === null || raw === undefined || raw === "") return {};
    if (typeof raw === "object") return raw;
    try {
        return JSON.parse(raw);
    } catch {
        return { _parseError: true, raw };
    }
}

function parseLocations(value) {
    if (!value) return [];
    return String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

function typeOf(v) {
    if (v === null) return "null";
    if (Array.isArray(v)) return "array";
    return typeof v;
}
function getArrayItemKey(item) {
    if (item && typeof item === "object" && !Array.isArray(item)) {
        if (typeof item.RenderKeyName === "string") return "rkn:" + item.RenderKeyName;
        if (item.Name !== undefined && (typeof item.Name === "string" || typeof item.Name === "number")) return "name:" + item.Name;
    }
    return undefined;
}

function canKeyArray(arr) {
    return arr.length > 0 && arr.every((item) => getArrayItemKey(item) !== undefined);
}

function diffArrayByKey(left, right) {
    const leftMap = new Map();
    const rightMap = new Map();
    left.forEach((item) => leftMap.set(getArrayItemKey(item), item));
    right.forEach((item) => rightMap.set(getArrayItemKey(item), item));

    const orderedKeys = [];
    const seen = new Set();
    left.forEach((item) => {
        const k = getArrayItemKey(item);
        if (!seen.has(k)) { seen.add(k); orderedKeys.push(k); }
    });
    right.forEach((item) => {
        const k = getArrayItemKey(item);
        if (!seen.has(k)) { seen.add(k); orderedKeys.push(k); }
    });

    return orderedKeys.map((k) => {
        const label = k.startsWith("rkn:") ? k.slice(4) : k.slice(5);
        return { key: label, ...diffValues(leftMap.get(k), rightMap.get(k)) };
    });
}

function diffValues(left, right) {
    const leftMissing = left === undefined;
    const rightMissing = right === undefined;

    if (leftMissing !== rightMissing) {
        const status = leftMissing ? "added" : "removed";
        const present = leftMissing ? right : left;
        const presentType = typeOf(present);

        if (presentType === "object" || presentType === "array") {
            const empty = presentType === "array" ? [] : {};
            const inner = leftMissing ? diffValues(empty, right) : diffValues(left, empty);
            return { status, left, right, children: inner.children };
        }

        return { status, left, right, children: null };
    }

    const lt = typeOf(left);
    const rt = typeOf(right);
    if (lt !== rt) return { status: "changed", left, right, children: null };

    if (lt === "object") {
        const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)])).sort();
        const children = keys.map((key) => ({ key, ...diffValues(left[key], right[key]) }));
        const status = children.every((c) => c.status === "same") ? "same" : "parent";
        return { status, left, right, children };
    }

    if (lt === "array") {
        const keyFor = (item, i) =>
            item && typeof item === "object" && typeof item.RenderKeyName === "string"
                ? `k:${item.RenderKeyName}`
                : `i:${i}`;

        const leftKeys = left.map(keyFor);
        const rightKeys = right.map(keyFor);
        const leftMap = new Map(left.map((v, i) => [leftKeys[i], v]));
        const rightMap = new Map(right.map((v, i) => [rightKeys[i], v]));

        const orderedKeys = [...leftKeys, ...rightKeys.filter((k) => !leftKeys.includes(k))];
        const seen = new Set();
        const uniqueKeys = orderedKeys.filter((k) => (seen.has(k) ? false : (seen.add(k), true)));

        const children = uniqueKeys.map((k) => ({
            key: k.startsWith("k:") ? k.slice(2) : `[${k.slice(2)}]`,
            ...diffValues(leftMap.get(k), rightMap.get(k)),
        }));
        const status = children.every((c) => c.status === "same") ? "same" : "parent";
        return { status, left, right, children };
    }

    const same = left === right;
    return { status: same ? "same" : "changed", left, right, children: null };
}

function countDiffs(node, acc = { added: 0, removed: 0, changed: 0 }) {
    if (!node) return acc;
    if (node.status === "added") acc.added++;
    else if (node.status === "removed") acc.removed++;
    else if (node.status === "changed") acc.changed++;
    if (node.children) node.children.forEach((c) => countDiffs(c, acc));
    return acc;
}

function formatPrimitive(v) {
    if (v === undefined) return "—";
    if (v === null) return "null";
    if (typeof v === "string") return `"${v}"`;
    return String(v);
}

function existsOn(side, node) {
    if (side === "left" && node.status === "added") return false;
    if (side === "right" && node.status === "removed") return false;
    return true;
}

function textMatches(value, term, exact) {
    return exact ? value === term : value.includes(term);
}
function buildSearchMatchSet(diffTree, term, exact) {
    const matched = new Set();
    function walk(node, path) {
        let selfMatch = node.key !== undefined && textMatches(String(node.key).toLowerCase(), term, exact);
        let childMatch = false;
        if (node.children) {
            node.children.forEach((c) => {
                const cPath = path ? path + "." + c.key : c.key;
                if (walk(c, cPath)) childMatch = true;
            });
        } else {
            const leftStr = formatPrimitive(node.left).toLowerCase();
            const rightStr = formatPrimitive(node.right).toLowerCase();
            if (textMatches(leftStr, term, exact) || textMatches(rightStr, term, exact)) selfMatch = true;
        }
        const isMatch = selfMatch || childMatch;
        if (isMatch) matched.add(path);
        return isMatch;
    }
    if (diffTree.children) {
        diffTree.children.forEach((c) => walk(c, c.key));
    } else {
        walk(diffTree, "root");
    }
    return matched;
}
function buildStatusMatchSet(diffTree, type) {
    const matched = new Set();
    function walk(node, path) {
        let childMatch = false;
        if (node.children) {
            node.children.forEach((c) => {
                const cPath = path ? path + "." + c.key : c.key;
                if (walk(c, cPath)) childMatch = true;
            });
        }
        const isMatch = node.status === type || childMatch;
        if (isMatch) matched.add(path);
        return isMatch;
    }
    if (diffTree.children) {
        diffTree.children.forEach((c) => walk(c, c.key));
    } else {
        walk(diffTree, "root");
    }
    return matched;
}

function highlightMatch(text, term, exact) {
    if (!term) return text;
    const lower = text.toLowerCase();

    if (exact) {
        return lower === term ? <mark className="jcm-mark">{text}</mark> : text;
    }

    const idx = lower.indexOf(term);
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark className="jcm-mark">{text.slice(idx, idx + term.length)}</mark>
            {text.slice(idx + term.length)}
        </>
    );
}
const TreeNode = React.memo(function TreeNode({
    node, side, depth, path, searchTerm, exactMatch, onlyDiffs, filterType,
    matchedPaths, statusPaths, expandEpoch, collapseEpoch,
}) {
    const [localCollapsed, setLocalCollapsed] = useState(false);
    const seenExpandEpoch = useRef(expandEpoch);
    const seenCollapseEpoch = useRef(collapseEpoch);

    useEffect(() => {
        if (expandEpoch !== seenExpandEpoch.current) {
            seenExpandEpoch.current = expandEpoch;
            setLocalCollapsed(false);
        }
    }, [expandEpoch]);

    useEffect(() => {
        if (collapseEpoch !== seenCollapseEpoch.current) {
            seenCollapseEpoch.current = collapseEpoch;
            setLocalCollapsed(true);
        }
    }, [collapseEpoch]);

    if (!existsOn(side, node)) return null;
    if (searchTerm && !matchedPaths.has(path)) return null;
    if (onlyDiffs && node.status === "same") return null;
    if (filterType && !statusPaths.has(path)) return null;

    const isBranch = node.children !== null;
    const value = side === "left" ? node.left : node.right;
    const forceExpand = !!searchTerm || onlyDiffs || !!filterType;
    const collapsed = forceExpand ? false : localCollapsed;

    let rowClass = "jcm-row";
    let badge = null;

    if (side === "right") {
        if (node.status === "added") { rowClass += " jcm-row--added"; badge = "new"; }
        else if (node.status === "changed") { rowClass += " jcm-row--changed"; badge = "changed"; }
    } else {
        if (node.status === "removed") { rowClass += " jcm-row--removed-marker"; badge = "removed"; }
        else if (node.status === "changed") { rowClass += " jcm-row--changed-marker"; }
    }

    if (isBranch) {
        const isArray = typeOf(value) === "array";
        const openBrace = isArray ? "[" : "{";
        const closeBrace = isArray ? "]" : "}";
        return (
            <div>
                <div className={rowClass} style={{ paddingLeft: depth * 12 }}>
                    <button type="button" className="jcm-toggle" onClick={() => setLocalCollapsed((c) => !c)} aria-label="Toggle">
                        <ChevronDown size={12} className={collapsed ? "jcm-caret jcm-caret--closed" : "jcm-caret"} />
                    </button>
                    {node.key !== undefined && (
                        <span className="jcm-key">{highlightMatch(String(node.key), searchTerm, exactMatch)}</span>
                    )}
                    {node.key !== undefined && <span className="jcm-colon">:</span>}
                    <span className="jcm-brace">{openBrace}</span>
                    {collapsed && <span className="jcm-hint">{node.children.length} items {closeBrace}</span>}
                </div>
                {!collapsed && (
                    <div>
                        {node.children.map((child) => (
                            <TreeNode
                                key={child.key}
                                node={child}
                                side={side}
                                depth={depth + 1}
                                path={path + "." + child.key}
                                searchTerm={searchTerm}
                                exactMatch={exactMatch}
                                onlyDiffs={onlyDiffs}
                                filterType={filterType}
                                matchedPaths={matchedPaths}
                                statusPaths={statusPaths}
                                expandEpoch={expandEpoch}
                                collapseEpoch={collapseEpoch}
                            />
                        ))}
                        <div className="jcm-row" style={{ paddingLeft: depth * 12 + 16 }}>
                            <span className="jcm-brace">{closeBrace}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const valueText = formatPrimitive(value);
    return (
        <div className={rowClass} style={{ paddingLeft: depth * 12 + 16 }}>
            <span className="jcm-key">{highlightMatch(String(node.key), searchTerm, exactMatch)}</span>
            <span className="jcm-colon">:</span>
            <span className="jcm-value">{highlightMatch(valueText, searchTerm, exactMatch)}</span>
            {side === "right" && node.status === "changed" && (
                <span className="jcm-was">was {formatPrimitive(node.left)}</span>
            )}
            {badge && <span className={`jcm-badge jcm-badge--${node.status}`}>{badge}</span>}
        </div>
    );
});

// Small reusable "copy JSON to clipboard" button used in each column header.
function CopyJsonButton({ value, label }) {
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef(null);

    useEffect(() => () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }, []);

    const handleCopy = useCallback(async (e) => {
        e.stopPropagation();
        try {
            const text = JSON.stringify(value ?? {}, null, 2);
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback for browsers/contexts without the async Clipboard API
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }
            setCopied(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard write failed (e.g. permissions) — silently ignore,
            // button simply won't show the "Copied" state.
        }
    }, [value]);

    return (
        <button
            type="button"
            className={`jcm-copy-btn${copied ? " jcm-copy-btn--copied" : ""}`}
            onClick={handleCopy}
            title={`Copy ${label} JSON to clipboard`}
        >
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

export default function JsonCompareModal({ isOpen, onClose, domain, row, title }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailVersion, setDetailVersion] = useState(0); 
    const [searchInput, setSearchInput] = useState("");
    const [exactMatch, setExactMatch] = useState(false);
    const [onlyDiffs, setOnlyDiffs] = useState(false);
    const [filterType, setFilterType] = useState(null); 
    const [expandEpoch, setExpandEpoch] = useState(0);
    const [collapseEpoch, setCollapseEpoch] = useState(0);

    const requestIdRef = useRef(0);
    const leftColRef = useRef(null);
    const rightColRef = useRef(null);
    const syncingRef = useRef(null); 

    const onLeftScroll = useCallback(() => {
        if (syncingRef.current === "right") { syncingRef.current = null; return; }
        if (!leftColRef.current || !rightColRef.current) return;
        syncingRef.current = "left";
        rightColRef.current.scrollTop = leftColRef.current.scrollTop;
    }, []);
    const onRightScroll = useCallback(() => {
        if (syncingRef.current === "left") { syncingRef.current = null; return; }
        if (!leftColRef.current || !rightColRef.current) return;
        syncingRef.current = "right";
        leftColRef.current.scrollTop = rightColRef.current.scrollTop;
    }, []);

    useEffect(() => {
        if (!isOpen || !row) return;
        setError(null);
        setSearchInput("");
        setExactMatch(false);
        setOnlyDiffs(false);
        setFilterType(null);
        setExpandEpoch(0);
        setCollapseEpoch(0);

        if (row.defaultJson !== undefined && row.savedJson !== undefined) {
            setDetail({ defaultJson: row.defaultJson, savedJson: row.savedJson });
            setDetailVersion((v) => v + 1);
            return;
        }

        if (!row.eventID) {
            setError("Missing Event ID for this row");
            return;
        }

        const thisRequestId = ++requestIdRef.current;
        setLoading(true);
        setDetail(null);

        getVisitNoteJsonDetails(domain, row.eventID)
            .then((res) => {
                if (requestIdRef.current !== thisRequestId) return; 

                const data = res?.data;
                if (!data) {
                    setError("No JSON details found for this event");
                    return;
                }

                const defaultJson = safeParseJson(data.jsonTemplateData);
                const savedJson = safeParseJson(data.jsonData);
                setDetail({ defaultJson, savedJson });
                setDetailVersion((v) => v + 1);
            })
            .catch((err) => {
                if (requestIdRef.current !== thisRequestId) return;

                if (err?.response?.status === 404) {
                    setError("No JSON details found for this event");
                } else {
                    setError(err.response?.data?.message || "Failed to load JSON details");
                }
            })
            .finally(() => {
                if (requestIdRef.current === thisRequestId) setLoading(false);
            });
    }, [isOpen, row, domain]);

    const diffTree = useMemo(() => {
        if (!detail) return null;
        try {
            return diffValues(detail.defaultJson || {}, detail.savedJson || {});
        } catch {
            return null;
        }
    }, [detail]);

    const stats = useMemo(() => (diffTree ? countDiffs(diffTree) : null), [diffTree]);
    const totalDiffCount = stats ? stats.added + stats.removed + stats.changed : 0;

    const searchTerm = searchInput.trim().toLowerCase();

    const matchedPaths = useMemo(() => {
        if (!searchTerm || !diffTree) return null;
        return buildSearchMatchSet(diffTree, searchTerm, exactMatch);
    }, [diffTree, searchTerm, exactMatch]);

    const statusPaths = useMemo(() => {
        if (!filterType || !diffTree) return null;
        return buildStatusMatchSet(diffTree, filterType);
    }, [diffTree, filterType]);

    const hasSearchMatches = !searchTerm || !diffTree || (matchedPaths ? matchedPaths.size > 0 : true);
    const hasDiffMatches = useMemo(() => {
        if (!diffTree) return true;
        if (onlyDiffs && diffTree.status === "same") return false;
        if (filterType && statusPaths && statusPaths.size === 0) return false;
        return true;
    }, [diffTree, onlyDiffs, filterType, statusPaths]);

    const showResults = hasSearchMatches && hasDiffMatches;
    const filtersActive = Boolean(searchInput || onlyDiffs || filterType);

    const resetFilters = useCallback(() => {
        setSearchInput("");
        setOnlyDiffs(false);
        setFilterType(null);
    }, []);

    const toggleFilterType = useCallback((type) => {
        setFilterType((current) => (current === type ? null : type));
    }, []);

    const expandAll = useCallback(() => setExpandEpoch((e) => e + 1), []);
    const collapseAll = useCallback(() => setCollapseEpoch((e) => e + 1), []);

    const locations = useMemo(() => parseLocations(row?.parentRenderKeyNames), [row]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const modalContent = (
        <div className="jcm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="jcm-dialog" role="dialog" aria-modal="true" style={{
                width: window.innerWidth < 768 ? '100%' : '1600px',
                maxWidth: window.innerWidth < 768 ? '100%' : '1600px',
                maxHeight: window.innerWidth < 768 ? '95vh' : '92vh',
            }}>
                <div className="jcm-header">
                    <div className="jcm-header-left">
                        <div className="jcm-title">{title || "JSON Comparison"}</div>
                        <div className="jcm-subtitle">
                            {row?.caseNo ? `Case ${row.caseNo}` : ""}
                            {row?.eventID ? ` · Event ${row.eventID}` : ""}
                        </div>
                        {locations.length > 0 && (
                            <div className="jcm-locations">
                                {locations.map((loc, i) => (
                                    <span key={`${loc}-${i}`} className="jcm-location-chip">{loc}</span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="jcm-header-right">
                        {stats && (
                            <div className="jcm-stats" role="group" aria-label="Filter by change type">
                                <button
                                    type="button"
                                    className={`jcm-stat jcm-stat--added${filterType === "added" ? " jcm-stat--active" : ""}`}
                                    onClick={() => toggleFilterType("added")}
                                    title="Show only new keys"
                                >
                                    ● {stats.added} new
                                </button>
                                <button
                                    type="button"
                                    className={`jcm-stat jcm-stat--changed${filterType === "changed" ? " jcm-stat--active" : ""}`}
                                    onClick={() => toggleFilterType("changed")}
                                    title="Show only changed keys"
                                >
                                    ● {stats.changed} changed
                                </button>
                                <button
                                    type="button"
                                    className={`jcm-stat jcm-stat--removed${filterType === "removed" ? " jcm-stat--active" : ""}`}
                                    onClick={() => toggleFilterType("removed")}
                                    title="Show only removed keys"
                                >
                                    ● {stats.removed} removed
                                </button>
                            </div>
                        )}
                        <button type="button" className="jcm-close" onClick={onClose} aria-label="Close">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {!loading && !error && diffTree && (
                    <div className="jcm-toolbar">
    <div className="jcm-search-bar">
        <Search size={14} className="jcm-search-icon" />
        <input
            type="text"
            className="jcm-search-input"
            placeholder="Search JSON keys or values…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
        />
        {searchInput && (
            <button type="button" className="jcm-search-clear" onClick={() => setSearchInput("")} aria-label="Clear search">
                <X size={13} />
            </button>
        )}
        <label className="jcm-check-toggle">
            <input type="checkbox" checked={exactMatch} onChange={(e) => setExactMatch(e.target.checked)} />
            Exact
        </label>
        <label className="jcm-check-toggle" title="Hide identical keys">
            <input type="checkbox" checked={onlyDiffs} onChange={(e) => setOnlyDiffs(e.target.checked)} disabled={totalDiffCount === 0} />
            Only differences
        </label>
    </div>

    <div className="jcm-toolbar-actions">
        {filtersActive && (
            <button type="button" className="jcm-toolbar-btn" onClick={resetFilters}>
                <RotateCcw size={12} />
                Reset
            </button>
        )}
        <button type="button" className="jcm-toolbar-btn" onClick={expandAll}>
            <Maximize2 size={12} />
            Expand all
        </button>
        <button type="button" className="jcm-toolbar-btn" onClick={collapseAll}>
            <Minimize2 size={12} />
            Collapse all
        </button>
    </div>
</div>
                )}

                <div className="jcm-body">
                    {loading && (
                        <div className="jcm-state">
                            <RefreshCw size={16} style={{ animation: "jcm-spin 1s linear infinite" }} />
                            <span>Loading JSON details…</span>
                        </div>
                    )}
                    {!loading && error && (
                        <div className="jcm-state jcm-state--error">
                            <AlertTriangle size={16} />
                            <span>{error}</span>
                        </div>
                    )}
                    {!loading && !error && diffTree && !showResults && !hasSearchMatches && (
                        <div className="jcm-state">
                            <Search size={16} />
                            <span>No matches for "{searchInput}"</span>
                        </div>
                    )}
                    {!loading && !error && diffTree && !showResults && hasSearchMatches && !hasDiffMatches && (
                        <div className="jcm-state jcm-state--success">
                            <CheckCircle2 size={16} />
                            <span>No differences found{filterType ? ` for "${filterType}"` : ""}</span>
                        </div>
                    )}
                    {!loading && !error && diffTree && showResults && (
                        <div className="jcm-compare-grid" key={detailVersion}>
                            <div className="jcm-col" ref={leftColRef} onScroll={onLeftScroll}>
                                <div className="jcm-col-head">
                                    <span className="jcm-col-head-label">
                                        <span className="jcm-dot jcm-dot--default" />Default
                                    </span>
                                    <CopyJsonButton value={detail?.defaultJson} label="Default" />
                                </div>
                                <div className="jcm-col-body">
                                    {diffTree.children
                                        ? diffTree.children.map((child) => (
                                            <TreeNode key={child.key} node={child} side="left" depth={0} path={child.key} searchTerm={searchTerm} exactMatch={exactMatch} onlyDiffs={onlyDiffs} filterType={filterType} matchedPaths={matchedPaths} statusPaths={statusPaths} expandEpoch={expandEpoch} collapseEpoch={collapseEpoch} />
                                        ))
                                        : <TreeNode node={diffTree} side="left" depth={0} path="root" searchTerm={searchTerm} exactMatch={exactMatch} onlyDiffs={onlyDiffs} filterType={filterType} matchedPaths={matchedPaths} statusPaths={statusPaths} expandEpoch={expandEpoch} collapseEpoch={collapseEpoch} />}
                                </div>
                            </div>
                            <div className="jcm-col" ref={rightColRef} onScroll={onRightScroll}>
                                <div className="jcm-col-head">
                                    <span className="jcm-col-head-label">
                                        <span className="jcm-dot jcm-dot--saved" />Saved
                                    </span>
                                    <CopyJsonButton value={detail?.savedJson} label="Saved" />
                                </div>
                                <div className="jcm-col-body">
                                    {diffTree.children
                                        ? diffTree.children.map((child) => (
                                            <TreeNode key={child.key} node={child} side="right" depth={0} path={child.key} searchTerm={searchTerm} exactMatch={exactMatch} onlyDiffs={onlyDiffs} filterType={filterType} matchedPaths={matchedPaths} statusPaths={statusPaths} expandEpoch={expandEpoch} collapseEpoch={collapseEpoch} />
                                        ))
                                        : <TreeNode node={diffTree} side="right" depth={0} path="root" searchTerm={searchTerm} exactMatch={exactMatch} onlyDiffs={onlyDiffs} filterType={filterType} matchedPaths={matchedPaths} statusPaths={statusPaths} expandEpoch={expandEpoch} collapseEpoch={collapseEpoch} />}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .jcm-backdrop {
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 2000; padding: 12px; animation: jcm-fade-in 0.12s ease;
                }
                .jcm-dialog {
                    background: #fff; border-radius: 10px; width: 100%; max-width: 1600px;
                    max-height: 92vh; display: flex; flex-direction: column; overflow: hidden;
                    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
                }
                @media (max-width: 768px) {
                    .jcm-dialog { max-height: 95vh; border-radius: 0; }
                }
                .jcm-header {
                    display: flex; align-items: flex-start; justify-content: space-between;
                    padding: 12px 16px; border-bottom: 1px solid #e9ecef; gap: 12px; flex-wrap: wrap;
                }
                .jcm-header-left { min-width: 0; flex: 1 1 auto; }
                .jcm-title { font-size: 15px; font-weight: 700; color: #212529; }
                .jcm-subtitle { font-size: 12px; color: #6c757d; margin-top: 2px; }
                .jcm-header-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
                .jcm-stats { display: flex; gap: 6px; flex-wrap: wrap; }
                .jcm-stat {
                    font-family: 'SF Mono', Consolas, Menlo, monospace; font-size: 11px;
                    padding: 3px 8px; border-radius: 5px; border: 1px solid #e9ecef;
                    cursor: pointer; transition: transform 0.08s ease, box-shadow 0.08s ease;
                }
                .jcm-stat:hover { filter: brightness(0.97); }
                .jcm-stat--active { box-shadow: 0 0 0 2px currentColor inset; font-weight: 700; }
                .jcm-stat--added { color: ${BRAND_GREEN}; border-color: #b2e8d8; background: #e6f7f2; }
                .jcm-stat--removed { color: #e02424; border-color: #f6c9c9; background: #fde8e8; }
                .jcm-stat--changed { color: #a86c00; border-color: #f2dfa8; background: #fdf3d8; }
                .jcm-close {
                    background: none; border: 1px solid #dee2e6; border-radius: 6px;
                    width: 28px; height: 28px; display: flex; align-items: center; justify-content: center;
                    color: #6c757d; cursor: pointer;
                }
                .jcm-close:hover { background: #f8f9fa; color: #212529; }

                /* Locations chips shown under the case/event subtitle */
                .jcm-locations {
                    display: flex; align-items: center; flex-wrap: wrap; gap: 5px;
                    margin-top: 6px;
                }
                .jcm-location-chip {
                    font-size: 10.5px; font-weight: 600; color: ${BRAND_GREEN};
                    background: #e6f7f2; border: 1px solid #b2e8d8; border-radius: 3px;
                    padding: 1px 7px;
                }

                /* Toolbar: search + filter toggles on one row, expand/collapse/reset on the other side */
                .jcm-toolbar {
                    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap;
                    gap: 8px; padding: 10px 16px; border-bottom: 1px solid #e9ecef; background: #fcfcfc;
                }
                .jcm-search-bar { display: flex; align-items: center; gap: 8px; flex: 1 1 320px; min-width: 220px; }
                .jcm-search-icon { color: #adb5bd; flex-shrink: 0; }
                .jcm-search-input {
                    flex: 1; border: none; outline: none; background: transparent;
                    font-size: 13px; color: #212529; min-width: 100px;
                }
                .jcm-search-input::placeholder { color: #adb5bd; }
                .jcm-search-clear {
                    background: none; border: none; color: #adb5bd; cursor: pointer;
                    display: flex; align-items: center; justify-content: center; padding: 2px; flex-shrink: 0;
                }
                .jcm-search-clear:hover { color: #212529; }
                .jcm-mark {
                    background: #fff3a3; color: inherit; border-radius: 2px; padding: 0 1px;
                }
                .jcm-check-toggle {
                    display: flex; align-items: center; gap: 5px;
                    font-size: 11.5px; color: #6c757d; cursor: pointer;
                    white-space: nowrap; user-select: none; flex-shrink: 0;
                }
                .jcm-check-toggle input[type="checkbox"] {
                    accent-color: ${BRAND_GREEN}; cursor: pointer; margin: 0;
                }

                .jcm-toolbar-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
                .jcm-toolbar-btn {
                    display: flex; align-items: center; gap: 5px;
                    font-size: 11.5px; font-weight: 600; color: #495057;
                    background: #fff; border: 1px solid #dee2e6; border-radius: 6px;
                    padding: 4px 9px; cursor: pointer; white-space: nowrap;
                }
                .jcm-toolbar-btn:hover { background: #f8f9fa; border-color: #ced4da; }

                .jcm-body { overflow: auto; padding: 12px 16px 16px; }
                .jcm-state {
                    display: flex; align-items: center; gap: 8px; justify-content: center;
                    padding: 40px 0; color: #6c757d; font-size: 13px;
                }
                .jcm-state--error { color: #e02424; }
                .jcm-state--success { color: ${BRAND_GREEN}; }

                .jcm-compare-grid {
    display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #e9ecef;
    border-radius: 8px; overflow: hidden;
}
@media (max-width: 768px) { 
    .jcm-compare-grid { 
        display: flex; 
        overflow-x: auto; 
        scroll-snap-type: x mandatory;
        gap: 12px;
    }
    .jcm-col { 
        flex: 0 0 100%; 
        scroll-snap-align: start;
        min-width: 100%;
    }
    .jcm-col + .jcm-col { border-left: none; }
}
                .jcm-compare-grid::-webkit-scrollbar { height: 6px; }
                .jcm-compare-grid::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .jcm-col { background: #fff; max-height: 68vh; overflow: auto; }
                .jcm-col + .jcm-col { border-left: 1px solid #e9ecef; }
                .jcm-col-head {
                    position: sticky; top: 0; z-index: 1; display: flex; align-items: center; justify-content: space-between;
                    gap: 6px; padding: 8px 10px 8px 12px; border-bottom: 1px solid #e9ecef; background: #fafafa;
                    font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #6c757d;
                }
                .jcm-col-head-label { display: flex; align-items: center; gap: 6px; }
                .jcm-dot { width: 7px; height: 7px; border-radius: 50%; }
                .jcm-dot--default { background: #4c8dff; }
                .jcm-dot--saved { background: ${BRAND_GREEN}; }
                .jcm-col-body { padding: 6px 0; }

                .jcm-copy-btn {
                    display: flex; align-items: center; gap: 4px;
                    font-size: 10px; font-weight: 700; text-transform: none; letter-spacing: 0;
                    color: #495057; background: #fff; border: 1px solid #dee2e6; border-radius: 5px;
                    padding: 3px 8px; cursor: pointer; flex-shrink: 0; transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
                }
                .jcm-copy-btn:hover { background: #f1f3f5; border-color: #ced4da; }
                .jcm-copy-btn--copied { color: ${BRAND_GREEN}; border-color: #b2e8d8; background: #e6f7f2; }

                .jcm-row {
                    display: flex; align-items: center; font-family: 'SF Mono', Consolas, Menlo, monospace;
                    font-size: 12px; line-height: 1.85; white-space: nowrap; padding-right: 10px;
                    border-left: 3px solid transparent; color: #212529;
                }
                .jcm-row:hover { filter: brightness(0.97); }
                .jcm-row--added { background: #e6f7f2; border-left-color: ${BRAND_GREEN}; }
                .jcm-row--changed { background: #fdf3d8; border-left-color: #d29922; }
                .jcm-row--removed-marker { background: #fde8e8; border-left-color: #f0a3a3; opacity: 0.8; }
                .jcm-row--changed-marker { border-left-color: #f2dfa8; }

                .jcm-toggle {
                    background: none; border: none; color: #adb5bd; cursor: pointer; width: 16px;
                    display: flex; align-items: center; justify-content: center; padding: 0;
                }
                .jcm-caret { transition: transform 0.12s; }
                .jcm-caret--closed { transform: rotate(-90deg); }
                .jcm-key { color: #1a6fd4; margin-left: 4px; }
                .jcm-colon { color: #adb5bd; margin-right: 6px; }
                .jcm-brace { color: #adb5bd; }
                .jcm-hint { color: #adb5bd; font-size: 10.5px; margin-left: 8px; font-style: italic; }
                .jcm-value { color: #333; }
                .jcm-was { color: #adb5bd; font-size: 10px; margin-left: 10px; font-style: italic; }

                .jcm-badge {
                    font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;
                    padding: 1px 6px; border-radius: 4px; margin-left: 10px;
                }
                .jcm-badge--added { background: #d3f0e6; color: ${BRAND_GREEN}; }
                .jcm-badge--changed { background: #f7e6b8; color: #a86c00; }
                .jcm-badge--removed { background: #f9d5d5; color: #e02424; }
            `}</style>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
}